import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  getCanFetchAssignmentPostSubmissionComments,
  getCanFetchAssignmentPreSubmissionComments,
} from '../../../assignment/helpers';
import { useCurrentUserWithRole } from '../../../courses/hooks';
import { setCommentContext } from '../../../db/comments/actions';
import { selectContextData } from '../../../db/comments/selectors';
import { CommentContextData } from '../../../db/comments/types';
import { AssignmentSubContext, CommentContext } from '../../../db/shared/types';
import { i18nNS } from '../../../i18n';
import { useAppSelector } from '../../../store/hooks';
import { CommentsTabMeta } from '../vm';

interface CommentTab {
  id: string;
  'aria-controls': string;
  title: string;
  isActive: boolean;
  isDisabled: boolean;
  context: CommentContextData;
}

export const CommentsTabIds = {
  [AssignmentSubContext.PRE_SUBMISSION]: 'assignment-pre-submission-tab',
  [AssignmentSubContext.POST_SUBMISSION]: 'assignment-post-submission-tab',
};

export const CommentsTabPanelIds = {
  [AssignmentSubContext.PRE_SUBMISSION]: 'assignment-pre-submission-tabpanel',
  [AssignmentSubContext.POST_SUBMISSION]: 'assignment-post-submission-tabpanel',
};

export interface UseCommentsTabs {
  setSelectedTabMeta: (meta: CommentsTabMeta | null) => void;
}

const useCommentsTabsVM = ({ setSelectedTabMeta }: UseCommentsTabs) => {
  const { t } = useTranslation([i18nNS.COMMENTS]);

  const dispatch = useDispatch();

  const contextData = useAppSelector((state) => selectContextData(state));

  const currentUser = useCurrentUserWithRole();

  const tabs: CommentTab[] | null = useMemo(() => {
    if (!contextData) return null;

    if (contextData?.type === CommentContext.ASSIGNMENT) {
      return [
        {
          id: CommentsTabIds[AssignmentSubContext.PRE_SUBMISSION],
          'aria-controls': CommentsTabPanelIds[AssignmentSubContext.PRE_SUBMISSION],
          title: t('pre_submission', { ns: i18nNS.COMMENTS }),
          isActive: contextData.subType === AssignmentSubContext.PRE_SUBMISSION,
          isDisabled: !getCanFetchAssignmentPreSubmissionComments(),
          context: {
            ...contextData,
            subType: AssignmentSubContext.PRE_SUBMISSION,
          },
        },
        {
          id: CommentsTabIds[AssignmentSubContext.POST_SUBMISSION],
          'aria-controls': CommentsTabPanelIds[AssignmentSubContext.POST_SUBMISSION],
          title: t('post_submission', { ns: i18nNS.COMMENTS }),
          isActive: contextData.subType === AssignmentSubContext.POST_SUBMISSION,
          isDisabled: contextData.activity
            ? !getCanFetchAssignmentPostSubmissionComments({
                assignment: contextData.activity,
                role: currentUser.role,
                userId: currentUser.userId,
              })
            : true,
          context: {
            ...contextData,
            subType: AssignmentSubContext.POST_SUBMISSION,
          },
        },
      ];
    }

    return null;
  }, [contextData, currentUser.role, currentUser.userId, t]);

  const activeTab = useMemo(() => tabs?.find((t) => t.isActive), [tabs]);

  const handleTabChange = (contextData: CommentContextData) => dispatch(setCommentContext(contextData));

  useEffect(
    function onActiveTabChange() {
      if (!activeTab) return;
      if (
        !(
          activeTab.context.subType === AssignmentSubContext.PRE_SUBMISSION ||
          activeTab.context.subType === AssignmentSubContext.POST_SUBMISSION
        )
      )
        return;
      setSelectedTabMeta({
        tabId: CommentsTabIds[activeTab.context.subType],
        panelId: CommentsTabPanelIds[activeTab.context.subType],
      });
    },
    [setSelectedTabMeta, activeTab]
  );

  useEffect(
    function onMount() {
      return function cleanup() {
        setSelectedTabMeta(null);
      };
    },
    [setSelectedTabMeta]
  );

  return {
    tabs,
    activeTab,
    handleTabChange,
  };
};

export default useCommentsTabsVM;
